/* eslint-disable max-lines */
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  getIconPath,
  routerPush,
  getBrandInCaps,
  getAPIConfig,
  getSiteId,
  getDynamicBadgeQty,
  getStringAcronym,
  DIMENSION92_TEXT,
  urlContainsQuery,
} from '@tcp/core/src/utils';
import AddedtoBagDynamicStore from '@tcp/core/src/components/features/CnC/AddedToBag/container/AddedToBagDynamicStore.js';
import {
  getGAPageName,
  getCategoryIdAndName,
  setSessionStorage,
} from '@tcp/core/src/utils/utils.web';
import { currencyConversion } from '@tcp/core/src/components/features/CnC/CartItemTile/utils/utils';
import Notification from '@tcp/core/src/components/common/molecules/Notification';
import { KEY_CODES } from '@tcp/core/src/constants/keyboard.constants';
import ProductRating from '@tcp/core/src/components/features/browse/ProductDetail/molecules/ProductRating/ProductRating';
import Constants from '@tcp/core/src/components/common/molecules/Recommendations/container/Recommendations.constants';
import Button from '@tcp/core/src/components/common/atoms/Button';
import Col from '@tcp/core/src/components/common/atoms/Col';
import Row from '@tcp/core/src/components/common/atoms/Row';
import CustomBadge from '@tcp/core/src/components/features/browse/ProductListing/atoms/CustomBadge';
import { TOP_RATED, BEST_SELLER } from '@tcp/core/src/constants/badge.constants';
import productGridItemPropTypes, {
  productGridDefaultProps,
} from '../propTypes/ProductGridItemPropTypes';
import { getLocator, isClient, fireEnhancedEcomm } from '../../../../../../../utils';
import {
  getImagesToDisplay,
  getMapSliceForColorProductId,
  getRearrangedImgUrls,
} from '../utils/productsCommonUtils';
import withStyles from '../../../../../../common/hoc/withStyles';
import styles, {
  StyledItemContainerInner,
  StyledNoItemAvailableWrapper,
  StyledFavoriteEditItemButton,
  StyledProductImageContainer,
  StyledNoRatingSpaceMobileSNJ,
  StyledHorizontalRule,
  AtbIcon,
} from '../styles/ProductsGridItemV2.style';
import { ProductTitle, ProductPricesSection } from './ProductItemComponents';
import { AVAILABILITY } from '../../../../Favorites/container/Favorites.constants';
import { getCartItemInfo } from '../../../../../CnC/AddedToBag/util/utility';
import RecommendationsAbstractor from '../../../../../../../services/abstractors/common/recommendations';
import { SLASH_C_ROUTE } from '../../../../../../../constants/plp.constants';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '../../../../../../../utils/labels/labelUtilty';
import { GlobalActionsLabels } from '../../../../../../../utils/labels/globalActions.labels';
import { fileteredProductGridItemLabels } from './ProductGridItem.labels';
import { PLPLabels, PLP_LABELS } from '../../../../../../../utils/labels/browse/PLP.labels';
import {
  GLOBAL_COMMON_BROWSE_LABELS,
  globalCommonBrowseLabels,
} from '../../../../../../../utils/labels/globalCommonBrowseLabels.labels';
import { globalSortingLabels } from '../../../../../../../utils/labels/globalSorting.labels';
import {
  GLOBAL_GENERIC_LABELS,
  GlobalGenericLabels,
} from '../../../../../../../utils/labels/globalGeneric.labels';
import { FAVORITES_LABELS, FavoriteLabels } from '../../../../Favorites/labels/Favorites.labels';
import { SLP_SUB_LABELS, slpSubLabels } from '../../../../../../../utils/labels/browse/SLP.labels';

const getSiteIdWithDefault = () => {
  return getSiteId() || 'us';
};
const getSeoUrl = (seotoken) => {
  return `/p/${seotoken}`;
};

const getPdpAsPathurl = (
  isProductBrandOfSameDomain,
  pdpUrl,
  crossDomain,
  cookieToken,
  brandSpecificCrossDomain,
  brand,
  isCrossBrandMerchandisingEnabled,
  seoToken,
  siteId
) => {
  let pdpLinkPath = pdpUrl;
  if (seoToken) {
    pdpLinkPath = getSeoUrl(seoToken);
  }
  if (isProductBrandOfSameDomain) {
    return pdpLinkPath;
  }
  if (isCrossBrandMerchandisingEnabled)
    return `/${siteId}${pdpLinkPath}$brand=${brand.toUpperCase()}`;
  if (cookieToken)
    return `${brandSpecificCrossDomain[brand.toUpperCase()]}${pdpLinkPath}${
      urlContainsQuery(pdpUrl) ? '&' : '?'
    }ct=${cookieToken}`;
  return `${brandSpecificCrossDomain[brand.toUpperCase()]}${pdpLinkPath}`;
};

class ProductsGridItem extends React.PureComponent {
  globalActions = getLabelFromCopyOrFromTranslation('global.actions', GlobalActionsLabels);

  // eslint-disable-next-line react/forbid-prop-types
  static propTypes = { ...productGridItemPropTypes, forwardedRef: PropTypes.object };

  static defaultProps = { ...productGridDefaultProps };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  constructor(props) {
    super(props);
    const { colorsMap, generalProductId } = props.item;
    if (colorsMap) {
      this.colorsExtraInfo = {
        [colorsMap[0].color.name]: colorsMap[0].miscInfo,
      };
    }
    const colorProductId = colorsMap ? colorsMap[0].colorProductId : generalProductId;
    const {
      item: {
        productInfo: { pdpUrl, seoToken },
      },
      isCrossBrandMerchandisingEnabled,
      isFavoriteView,
      cookieToken,
    } = props;

    const labels = {
      ...{
        PLP: {
          plpTiles: getLabelFromCopyOrFromTranslation(PLP_LABELS, PLPLabels),
          seoText: getLabelFromCopyOrFromTranslation('PLP.seoText', PLPLabels),
        },
      },
      ...{
        global: {
          CommonBrowseLabels: getLabelFromCopyOrFromTranslation(
            GLOBAL_COMMON_BROWSE_LABELS,
            globalCommonBrowseLabels
          ),
          Sorting: getLabelFromCopyOrFromTranslation('global.Sorting', globalSortingLabels),
          generic: getLabelFromCopyOrFromTranslation(GLOBAL_GENERIC_LABELS, GlobalGenericLabels),
        },
      },

      ...getLabelFromCopyOrFromTranslation(FAVORITES_LABELS, FavoriteLabels),
      ...getLabelFromCopyOrFromTranslation(SLP_SUB_LABELS, slpSubLabels),
    };
    this.productGridItemLabels = fileteredProductGridItemLabels(labels);
    this.favLabels = getLabelFromCopyOrFromTranslation(FAVORITES_LABELS, FavoriteLabels);
    this.commmonBrowseLabels = getLabelFromCopyOrFromTranslation(
      GLOBAL_COMMON_BROWSE_LABELS,
      globalCommonBrowseLabels
    );
    const currentSiteBrand = getBrandInCaps();
    const apiConfigObj = getAPIConfig();
    const { brandSpecificCrossDomain } = apiConfigObj;
    const itemBrand = props.item?.itemInfo?.brand
      ? props.item.itemInfo.brand.toUpperCase()
      : currentSiteBrand;
    const crossDomain = brandSpecificCrossDomain[itemBrand];
    const isProductBrandOfSameSiteBrand =
      currentSiteBrand === itemBrand || (!isFavoriteView && isCrossBrandMerchandisingEnabled);
    const siteId = getSiteIdWithDefault();
    let itempdpUrl =
      props?.item?.productInfo?.pdpUrl?.startsWith(siteId) ||
      props?.item?.productInfo?.pdpUrl?.startsWith(`/${siteId}`)
        ? props.item.productInfo.pdpUrl.replace(siteId, '')
        : props?.item?.productInfo?.pdpUrl;
    const cd66 = props?.item?.dimension66;
    const cd81 = props?.item?.dimension81;
    if (isFavoriteView) {
      itempdpUrl = getPdpAsPathurl(
        isProductBrandOfSameSiteBrand,
        itempdpUrl,
        crossDomain,
        cookieToken,
        brandSpecificCrossDomain,
        itemBrand,
        isCrossBrandMerchandisingEnabled,
        seoToken,
        siteId
      );
    }
    if (cd66) {
      itempdpUrl = itempdpUrl.concat('?fromBIA=1');
    }
    if (cd81) {
      itempdpUrl = itempdpUrl.concat('?fromBIRTHDAY=1');
    }
    const getCrossBrandPDPURL = () => {
      return isFavoriteView && isCrossBrandMerchandisingEnabled
        ? `/${siteId}${itempdpUrl}$brand=${itemBrand?.toUpperCase()}`
        : `${crossDomain}/${getSiteId()}${pdpUrl}${cd81 ? `?fromBIRTHDAY=1` : ''}`;
    };
    this.state = {
      selectedColorProductId: colorProductId,
      currentImageIndex: 0,
      pdpUrl: isProductBrandOfSameSiteBrand || isFavoriteView ? itempdpUrl : getCrossBrandPDPURL(),
      isAltImgRequested: false,
      isMoveItemOpen: false,
      generalProductId: '',
      errorProductId: '',
      showDiscoveryIcon: false,
    };
    const { onQuickViewOpenClick, page } = this.props;
    const { selectedColorProductId } = this.state;
    this.handleOpenQuickViewClick = () =>
      onQuickViewOpenClick(generalProductId, selectedColorProductId, generalProductId, page);
    this.handleImageChange = (index) => this.setState({ currentImageIndex: index });
  }

  /**
   * This function returns array of images for carousal and also decides whether to show image carousal or not
   * @param {*} imageUrls
   */
  getImageCarouselOptions(imageUrls) {
    const { hideImageCarousel } = this.props;

    return hideImageCarousel ? imageUrls.slice(0, 1) : imageUrls;
  }

  handleOpenAltImages = () => {
    const { isAltImgRequested } = this.state;
    if (isAltImgRequested) {
      return;
    }
    this.setState({ isAltImgRequested: true });
  };

  renderCustomRatings = (renderWrapper = false) => {
    const {
      item,
      isHidePLPRatings,
      starRatingSize,
      isBagPage,
      isSNJEnabled,
      isNewPDPEnabled,
      theme,
      isNewPLPAtbCardTileEnabled,
      isConfirmationPage,
    } = this.props;
    const {
      productInfo: { ratings = 3, reviewsCount },
    } = item;

    let customRatings = null;
    if (!ratings && isSNJEnabled && isNewPDPEnabled) {
      customRatings = <StyledNoRatingSpaceMobileSNJ theme={theme} />;
    }
    customRatings = !isHidePLPRatings && !!ratings && (
      <ProductRating
        ratings={ratings || 0}
        reviews={reviewsCount}
        starRatingSize={starRatingSize}
        isBagPage={isBagPage}
        isNewPJPPDPRedesignEnabled={isNewPLPAtbCardTileEnabled}
        isConfirmationPage={isConfirmationPage}
      />
    );
    if (!customRatings) return null;

    return renderWrapper ? (
      <>
        {isNewPLPAtbCardTileEnabled && <StyledHorizontalRule />}
        <div className="plp-new-rating-wrapper"> {customRatings}</div>
      </>
    ) : (
      <>
        {isNewPLPAtbCardTileEnabled && <StyledHorizontalRule />}
        {customRatings}
      </>
    );
  };

  getDisplayPriceRange = (showPriceRange, isFavoriteView) => {
    return showPriceRange && !isFavoriteView;
  };

  /* function to get product price section */

  getProductPriceSection = (listPriceForColor, offerPriceForColor, badge3) => {
    const { item, currencyAttributes, showPriceRange, theme } = this.props;
    const bundleProduct = item && item.productInfo && item.productInfo.bundleProduct;
    let priceRange = item && item.productInfo && item.productInfo.priceRange;
    if (priceRange && currencyAttributes && currencyAttributes.exchangevalue) {
      priceRange = {
        highListPrice: currencyConversion(priceRange.highListPrice, currencyAttributes),
        highOfferPrice: currencyConversion(priceRange.highOfferPrice, currencyAttributes),
        lowListPrice: currencyConversion(priceRange.lowListPrice, currencyAttributes),
        lowOfferPrice: currencyConversion(priceRange.lowOfferPrice, currencyAttributes),
      };
    }
    const badge3Text = listPriceForColor - offerPriceForColor !== 0 ? badge3 : '';
    const { isFavoriteView, isSuggestedItem } = this.props;
    const displayPriceRange = this.getDisplayPriceRange(showPriceRange, isFavoriteView);

    return (
      <>
        <StyledFavoriteEditItemButton
          theme={theme}
          isItemView={isFavoriteView || isSuggestedItem}
        />
        <ProductPricesSection
          listPrice={listPriceForColor}
          offerPrice={offerPriceForColor}
          noMerchantBadge={badge3}
          merchantTag={badge3Text}
          hidePrefixListPrice
          bundleProduct={bundleProduct}
          priceRange={priceRange}
          showPriceRange={displayPriceRange}
        />
      </>
    );
  };

  getRecIdentifier = () => {
    let portalValue = null;
    const {
      page,
      variation = '',
      variations = '',
      moduleOHeaderLabel = '',
      modulePHeaderLabel = '',
    } = this.props;
    const moduleName = variation || variations || '';
    if (moduleName.includes('moduleO')) {
      portalValue = moduleOHeaderLabel;
    } else if (moduleName.includes('moduleP')) {
      portalValue = modulePHeaderLabel;
    }
    const recIdentifier = RecommendationsAbstractor.handleWhichRecommendationClicked(
      portalValue,
      page
    );
    return recIdentifier && recIdentifier.length ? recIdentifier : '';
  };

  categoryIdAndName = () => {
    const { item, categoryId = '', router = null } = this.props;
    const exactCategoryPath =
      (categoryId &&
        item.categoryPath2_catMap &&
        item.categoryPath2_catMap?.filter((categoryPath) => categoryPath.includes(categoryId))) ||
      '';
    return router?.query?.searchQuery
      ? router.query.searchQuery
      : `${item.miscInfo.categoryId}|${item.miscInfo.categoryName}` ||
          (exactCategoryPath && getCategoryIdAndName(exactCategoryPath)) ||
          '';
  };

  callAnalyticsOnAddToBagclick = () => {
    const { page, item, position } = this.props;
    const recIdentifier = this.getRecIdentifier();
    const recAcronym = this.getRecAcronym();
    const pageAcronym = getStringAcronym(page || '');
    const categoryIdAndName = this.categoryIdAndName();
    const currentSiteBrand = getBrandInCaps();
    const cd66 = item?.dimension66
      ? { dimension66: `${item?.dimension66}|position-${position + 1}` }
      : '';
    const cd81 = item?.dimension81
      ? { dimension81: `${item?.dimension81}|position-${position + 1}` }
      : '';

    const args = {
      eventName: 'productDetail',
      eventType: 'detail',
      pageName: page,
      eventLabel: 'quick view product detail',
      productsObj: [
        {
          id: item.productInfo.generalProductId,
          price: item.productInfo.offerPrice,
          name: item.productInfo.name,
          brand: currentSiteBrand,
          category: recIdentifier
            ? `${DIMENSION92_TEXT}${pageAcronym}-${recAcronym}`
            : categoryIdAndName,
          dimension20: item.productInfo.generalProductId,
          dimension21: page,
          dimension35: currentSiteBrand,
          dimension80: recIdentifier
            ? `${DIMENSION92_TEXT}${pageAcronym}-${recAcronym}`
            : categoryIdAndName,
          dimension89: item.productInfo.generalProductId,
          dimension92: recIdentifier ? `${DIMENSION92_TEXT}${pageAcronym}-${recAcronym}` : '',
          dimension2: recIdentifier
            ? Constants.RECOMMENDATIONS_PAGES_MAPPING.RECOMMENDATIONS
            : 'browse',
          metric50: '1',
          ...cd66,
          ...cd81,
        },
      ],
    };

    fireEnhancedEcomm(args);
  };

  onQuickViewOpenClick = (payload) => {
    const { onQuickViewOpenClick } = this.props;
    onQuickViewOpenClick(payload);
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  handleQuickViewOpenClick = async (e) => {
    const {
      isSuggestedItem,
      onReplaceWishlistItem,
      activeWishListId,
      suggestedOOSItemId,
      item,
      addToBagEcom,
      isFavoriteView,
      page,
      position,
      resetSlpLoadingState,
      alternateBrandsShown,
      acrossBrandApiCalled,
    } = this.props;

    await AddedtoBagDynamicStore();
    if (acrossBrandApiCalled) {
      resetSlpLoadingState({ alternateBrandName: alternateBrandsShown });
    }
    if (e) {
      e.preventDefault();
    }
    const {
      productInfo: { generalProductId },
      skuInfo: { skuId, size } = {},
      itemInfo: { brand } = {},
    } = item;
    const { selectedColorProductId } = this.state;
    const cd66 = item?.dimension66
      ? { dimension66: `${item?.dimension66}|position-${position + 1}` }
      : '';
    const cd81 = item?.dimension81
      ? { dimension81: `${item?.dimension81}|position-${position + 1}` }
      : '';
    if (item?.dimension66) {
      const { trackAnalyticsClick } = this.props;
      trackAnalyticsClick({
        customEvents: ['event66', 'event63'],
        metric63: '1',
        evar66: cd66?.dimension66,
      });
    }
    if (item?.dimension81) {
      const { trackAnalyticsClick } = this.props;
      trackAnalyticsClick({
        customEvents: ['event116'],
        metric116: '1',
        evar81: cd81?.dimension81,
      });
    }
    if (isSuggestedItem && onReplaceWishlistItem) {
      const formData = {
        activeWishListId,
        itemId: suggestedOOSItemId,
        colorProductId: generalProductId,
      };
      onReplaceWishlistItem(formData);
    } else if (isFavoriteView) {
      const orderInfo = {
        itemBrand: brand,
      };
      if (skuId && size) {
        let cartItemInfo = getCartItemInfo(item, {});
        cartItemInfo = { ...cartItemInfo, brand, fromFavorites: true };
        if (addToBagEcom) addToBagEcom(cartItemInfo);
      } else {
        this.onQuickViewOpenClick({
          colorProductId: skuId,
          orderInfo,
          fromPage: page,
        });
      }
    } else {
      this.onQuickViewOpenClick({
        colorProductId: selectedColorProductId,
        fromPage: page,
        ...cd66,
        ...cd81,
      });
    }
    this.callAnalyticsOnAddToBagclick();
  };

  handleViewBundleClick = (e) => {
    const {
      isBundleProductABTest,
      item: {
        productInfo: { bundleProduct, pdpUrl },
      },
      alternateBrand,
    } = this.props;

    const newPdpUrl = alternateBrand ? `${pdpUrl}$brand=${alternateBrand}` : `${pdpUrl}`;
    const isBundleProduct = !isBundleProductABTest && bundleProduct;
    if (isBundleProduct && pdpUrl) {
      routerPush(pdpUrl.replace('b/', 'b?bid='), newPdpUrl);
    } else {
      this.handleQuickViewOpenClick(e);
    }
  };

  errorMsgDisplay = () => {
    const {
      errorMessages,
      AddToFavoriteErrorMsg,
      item: {
        productInfo: { generalProductId },
      },
    } = this.props;
    const errorMsg = errorMessages && errorMessages[generalProductId];

    return errorMsg && errorMsg.itemId === generalProductId && AddToFavoriteErrorMsg ? (
      <Notification
        status="error"
        colSize={{ large: 12, medium: 8, small: 6 }}
        message={AddToFavoriteErrorMsg}
      />
    ) : null;
  };

  renderSuggestedError = (
    hasSuggestedProduct,
    suggestedProductErrors,
    generalProductId,
    item,
    isFavoriteView
  ) => {
    if (!isFavoriteView) {
      return false;
    }

    if (!item.skuInfo) {
      return false;
    }
    const {
      skuInfo: { colorProductId },
    } = item;

    if (!colorProductId) {
      return null;
    }
    const errorLabel =
      this.favLabels &&
      renderLabel(
        this.favLabels.lbl_fav_see_suggested_exception,
        'lbl_fav_see_suggested_exception'
      );
    return !hasSuggestedProduct &&
      colorProductId &&
      suggestedProductErrors &&
      suggestedProductErrors === colorProductId ? (
      // eslint-disable-next-line react/jsx-indent
      <Notification
        status="error"
        colSize={{ large: 12, medium: 8, small: 6 }}
        message={errorLabel}
      />
    ) : null;
  };

  getColorMiscInfo = (curentColorEntry) => {
    return (
      (curentColorEntry &&
        (this.colorsExtraInfo[curentColorEntry.color.name] || curentColorEntry.miscInfo)) ||
      {}
    );
  };

  onkeyPress = (e) => {
    const {
      item: {
        productInfo: { bundleProduct },
      },
      isShowQuickView,
    } = this.props;
    const isBundleProduct = bundleProduct;
    const { KEY_ENTER, KEY_SPACE } = KEY_CODES;
    if (e.keyCode === KEY_ENTER || e.keyCode === KEY_SPACE) {
      e.preventDefault();
      if (isShowQuickView && !isBundleProduct) {
        this.handleQuickViewOpenClick();
      } else {
        this.handleViewBundleClick();
      }
    }
  };

  renderSubmitButton = (keepAlive, itemNotAvailable, isHidePLPAddToBag) => {
    // DRP-43: Kill Switch to hide Add to Bag CTA on PLP and SLP.
    // Added this condition here, as main render method is reaching higher complexity
    // Hide when response is false
    if (isHidePLPAddToBag) {
      return '';
    }
    const {
      theme,
      item: {
        itemInfo: { itemId } = {},
        productInfo: { bundleProduct, generalProductId },
      },
      removeFavItem,

      isShowQuickView,
      AddToFavoriteErrorMsg,
      item,
    } = this.props;

    const { errorProductId } = this.state;
    const fulfilmentSection =
      AddToFavoriteErrorMsg && errorProductId === generalProductId ? '' : 'fulfillment-section';
    const isBundleProduct = bundleProduct;

    return itemNotAvailable ? (
      <StyledNoItemAvailableWrapper theme={theme} removeFavItem={removeFavItem}>
        <Button
          className="remove-favorite"
          fullWidth
          buttonVariation="fixed-width"
          dataLocator={getLocator('remove_favorite_Button')}
          onClick={() => removeFavItem({ itemId, products: item.productInfo })}
        >
          {renderLabel(this.globalActions.lbl_remove, 'lbl_remove')}
        </Button>
      </StyledNoItemAvailableWrapper>
    ) : (
      <div className={fulfilmentSection}>
        <AtbIcon
          onKeyUp={this.onkeyPress}
          disabled={keepAlive}
          onClick={
            isShowQuickView && !isBundleProduct
              ? this.handleQuickViewOpenClick
              : this.handleViewBundleClick
          }
        >
          <img src={getIconPath('add-to-bag')} alt="Add to bag" />
        </AtbIcon>
      </div>
    );
  };

  getIsProductBrandSameDomain = () => {
    const { item } = this.props;
    const currentSiteBrand = getBrandInCaps();
    const itemBrand = item?.itemInfo?.brand ? item.itemInfo.brand.toUpperCase() : currentSiteBrand;
    return currentSiteBrand === itemBrand;
  };

  renderProductTitle = ({
    name,
    pdpUrl,
    loadedProductCount,
    viaModule,
    analyticsData,
    isProductBrandOfSameDomain,
    isSearchPage,
    page,
    isSoldOut,
    cookieToken,
    bundleProduct,
    dataSource,
    googleAnalyticsData,
    primaryBrand,
    isSNJEnabled,
    isNewPDPEnabled,
    categoryIdToCheck,
    setCurrentCategoryIdActn,
    isFromPLPPage,
  }) => {
    const {
      compactView,
      alternateBrand,
      isShowBrandNameEnabled,
      isSearchListing,
      isRecommendation,
      isBopisFilterOn,
      isRecommendationProductInfoEnabled,
      isCrossBrandMerchandisingEnabled,
      isFavoriteView,
    } = this.props;
    return !compactView ||
      (isSNJEnabled && isNewPDPEnabled && !isRecommendationProductInfoEnabled) ? (
      <ProductTitle
        name={name}
        pdpUrl={pdpUrl}
        loadedProductCount={loadedProductCount}
        viaModule={viaModule}
        dataSource={dataSource}
        analyticsData={analyticsData}
        isProductBrandOfSameDomain={isProductBrandOfSameDomain}
        isSearchPage={isSearchPage}
        page={page}
        isSoldOut={isSoldOut}
        cookieToken={cookieToken}
        bundleProduct={bundleProduct}
        googleAnalyticsData={googleAnalyticsData}
        alternateBrand={alternateBrand}
        labels={this.productGridItemLabels}
        isShowBrandNameEnabled={isShowBrandNameEnabled}
        primaryBrand={primaryBrand || alternateBrand}
        isSearchListing={isSearchListing}
        isRecommendation={isRecommendation}
        isBopisFilterOn={isBopisFilterOn}
        categoryIdToCheck={categoryIdToCheck}
        setCurrentCategoryIdActn={setCurrentCategoryIdActn}
        isFromPLPPage={isFromPLPPage}
        isCrossBrandMerchandisingEnabled={isCrossBrandMerchandisingEnabled}
        isFavoriteView={isFavoriteView}
      />
    ) : null;
  };

  renderTopRatedBadgeItem = () => {
    const { theme } = this.props;

    return (
      <CustomBadge
        type={TOP_RATED}
        theme={theme}
        text={renderLabel(
          this.productGridItemLabels.topRatedBadgeLabel,
          'lbl_TCPBazaarVoiceRating'
        )}
        customFontWeight="bold"
        customFontSize={['fs8', 'fs8', 'fs10']}
      />
    );
  };

  renderBestSellerBadgeItem = () => {
    const { theme } = this.props;

    return (
      <CustomBadge
        type={BEST_SELLER}
        theme={theme}
        text={renderLabel(this.productGridItemLabels.bestSellerBadgeLabel, 'lbl_best_seller')}
        customFontWeight="bold"
        customFontSize={['fs8', 'fs8', 'fs10']}
      />
    );
  };

  getPriceForProduct = (listPrice, offerPrice, currencyAttributes) => {
    let listPriceForColor = listPrice;
    let offerPriceForColor = offerPrice;
    if (currencyAttributes && currencyAttributes.exchangevalue) {
      listPriceForColor = currencyConversion(listPrice, currencyAttributes);
      offerPriceForColor = currencyConversion(offerPrice, currencyAttributes);
    }
    return {
      listPriceForColor,
      offerPriceForColor,
    };
  };

  getRecAcronym = () => {
    const recIdentifier = this.getRecIdentifier();
    const recIdentifierMatch = (recIdentifier && recIdentifier.match(/\b(\w)/g)) || '';
    return (recIdentifierMatch && recIdentifierMatch.join('')) || '';
  };

  biaTrackCLickPDP = (cd66) => {
    const { pdpUrl } = this.state;
    const { trackAnalyticsClick } = this.props;
    trackAnalyticsClick({ customEvents: ['event63'], metric63: '1' });
    const cd66Item = JSON.stringify({
      dimension66: cd66,
      item: pdpUrl,
    });
    setSessionStorage({ key: 'biaDimension', value: cd66Item });
  };

  bdayTrackCLickPDP = (cd81) => {
    const { pdpUrl } = this.state;
    const { trackAnalyticsClick } = this.props;
    trackAnalyticsClick({ customEvents: ['event116', 'evar81'], metric116: '1' });
    const cd81Item = JSON.stringify({
      dimension81: cd81,
      item: pdpUrl,
    });
    setSessionStorage({ key: 'bdayDimension', value: cd81Item });
  };

  updateGoogleAnalyticsData = () => {
    const { item, currencyAttributes, position, currencySymbol, page } = this.props;
    let color = '';
    item?.colorsMap?.forEach((c) => {
      if (c.colorProductId === item.productInfo.generalProductId) color = c.color.name;
    });
    const currentSiteBrand = getBrandInCaps();
    const currency = (currencyAttributes && currencyAttributes.id) || currencySymbol || '';
    const isSearchPage = isClient() && window.location.href.includes('/search/');
    const recIdentifier = this.getRecIdentifier();
    const recAcronym = this.getRecAcronym();
    const pageAcronym = getStringAcronym(page || '');
    const categoryIdAndName = this.categoryIdAndName();
    const cd66 = item?.dimension66 ? `${item?.dimension66}|position-${position + 1}` : '';

    const cd81 = item?.dimension81 ? `${item?.dimension81}|position-${position + 1}` : '';
    if (item?.dimension81) {
      this.bdayTrackCLickPDP(cd81);
    }

    const args = {
      eventName: 'productclick',
      eventAction: 'Product Click',
      eventType: 'click',
      pageName: getGAPageName(),
      currCode: currency,
      eventLabel: 'Product detail',
      productsObj: [
        {
          name: item.productInfo.name,
          id: item.productInfo.generalProductId,
          price: item.productInfo.offerPrice,
          brand: currentSiteBrand,
          category: recIdentifier
            ? `${DIMENSION92_TEXT}${pageAcronym}-${recAcronym}`
            : categoryIdAndName,
          variant: color,
          position: isSearchPage ? position + 1 : position,
          dimension80: recIdentifier
            ? `${DIMENSION92_TEXT}${pageAcronym}-${recAcronym}`
            : categoryIdAndName,
          dimension67:
            item.productInfo.offerPrice < item.productInfo.listPrice ? 'on sale' : 'full price',
          dimension89: item.productInfo.generalProductId,
          dimension100: isSearchPage ? position + 1 : position,
          dimension63: `${item.productInfo.offerPrice} ${currency}: ${item.productInfo.listPrice} ${currency}`,
          dimension77: color,
          dimension35: currentSiteBrand,
          dimension92: recIdentifier ? `${DIMENSION92_TEXT}${pageAcronym}-${recAcronym}` : '',
          dimension21: page,
          ...cd66,
          ...cd81,
        },
      ],
    };

    fireEnhancedEcomm(args);
  };

  isPDP = () => {
    const { page } = this.props;
    return `${page === 'pdp' ? 'pdp' : ''}`;
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  renderCardTile = () => {
    const {
      currencyAttributes,
      item,
      loadedProductCount,
      sqnNmbr,
      unbxdId,
      viaModule,
      dataSource,
      isSearchPage,
      page,
      cookieToken,
      isSNJEnabled,
      isNewPDPEnabled,
      currentCategoryId,
      categoryId,
      setCurrentCategoryIdActn,
    } = this.props;
    const {
      miscInfo: { categoryId: categoryIdL3 },
      productInfo: {
        bundleProduct,
        generalProductId,
        name,
        listPrice: itemListPrice,
        offerPrice: itemOfferPrice,
        primaryBrand,
      },
      itemInfo: { availability } = {},
      colorsMap,
    } = item;

    const itemNotAvailable = availability === AVAILABILITY.SOLDOUT;
    const { selectedColorProductId, pdpUrl } = this.state;
    const curentColorEntry = getMapSliceForColorProductId(colorsMap, selectedColorProductId);
    const currentColorMiscInfo = this.getColorMiscInfo(curentColorEntry);
    const { listPrice = itemListPrice, offerPrice = itemOfferPrice, badge3 } = currentColorMiscInfo;
    const isFromPLPPage = isClient() && window?.location?.href?.includes(SLASH_C_ROUTE);
    const categoryIdToCheck = currentCategoryId || categoryIdL3 || categoryId;
    const { listPriceForColor, offerPriceForColor } = this.getPriceForProduct(
      listPrice,
      offerPrice,
      currencyAttributes
    );
    const sqnNmb = sqnNmbr;

    const isProductBrandOfSameDomain = this.getIsProductBrandSameDomain();
    const googleAnalyticsData = this.updateGoogleAnalyticsData;

    return (
      <>
        <Row fullBleed className="product-wishlist-container">
          {this.renderProductTitle({
            name,
            pdpUrl,
            loadedProductCount,
            viaModule,
            dataSource,
            analyticsData: {
              pId: generalProductId,
              prank: sqnNmb,
              requestId: unbxdId,
            },
            isProductBrandOfSameDomain,
            isSearchPage,
            page,
            isSoldOut: itemNotAvailable,
            cookieToken,
            bundleProduct,
            googleAnalyticsData,
            primaryBrand,
            isSNJEnabled,
            isNewPDPEnabled,
            categoryIdToCheck,
            setCurrentCategoryIdActn,
            isFromPLPPage,
          })}
          <Col colSize={{ small: 4, medium: 6, large: 10 }}>
            {this.getProductPriceSection(listPriceForColor, offerPriceForColor, badge3)}
          </Col>
          {this.renderCustomRatings()}
        </Row>
      </>
    );
  };

  // eslint-disable-next-line complexity
  // eslint-disable-next-line sonarjs/cognitive-complexity
  render() {
    const outOfStockCapsLabels = renderLabel(
      this.commmonBrowseLabels.lbl_common_outOfStockCaps,
      'lbl_common_outOfStockCaps'
    );
    const {
      item,
      isOnModelAbTestPlp,
      loadedProductCount,
      className,
      sqnNmbr,
      unbxdId,
      isFavoriteView,
      forwardedRef,
      isKeepAliveEnabled,
      isSuggestedItem,
      viaModule,
      dataSource,
      isSearchPage,
      page,
      cookieToken,
      isHidePLPAddToBag,
      hasSuggestedProduct,
      suggestedProductErrors,
      lowQualityEnabled,
      portalValue,
      isDynamicBadgeEnabled,
      inGridPlpRecommendation,
      widgetScrapeContentId,
      isSecureImageFlagEnabled,
      alternateBrand,
      isBopisFilterOn,
      theme,
      currentCategoryId,
      categoryId,
      setCurrentCategoryIdActn,
      isHorizontalPLPCarouselEnabled,
      isNewPLPImageBorderEnabled,
      isPLPImageHeightBasedOnAspectRatio,
      preloadImage,
      trackAnalyticsClick,
    } = this.props;
    const {
      miscInfo: { categoryId: categoryIdL3 },
      productInfo: {
        bundleProduct,
        generalProductId,
        name,
        long_product_title: longProductTitle,
        tcpStyleQty,
        tcpStyleType,
        primaryBrand,
      },
      itemInfo: { availability, brand } = {},
      colorsMap,
      imagesByColor,
      recToken,
      fromFavorites,
      imageURL,
    } = item;
    console.log('############# gridItem', recToken);

    const itemNotAvailable = availability === AVAILABILITY.SOLDOUT;
    const prodNameAltImages = longProductTitle || name;
    const { selectedColorProductId, pdpUrl } = this.state;
    const curentColorEntry = getMapSliceForColorProductId(colorsMap, selectedColorProductId);
    let imageUrls = getImagesToDisplay({
      imagesByColor,
      curentColorEntry,
      isAbTestActive: isOnModelAbTestPlp,
      isFavoriteView,
    });
    imageUrls = getRearrangedImgUrls(imageUrls, curentColorEntry);
    const imageUrlsToShow = this.getImageCarouselOptions(imageUrls);
    const currentColorMiscInfo = this.getColorMiscInfo(curentColorEntry);
    const { keepAlive: keepAliveProduct } = currentColorMiscInfo;
    const keepAlive = isKeepAliveEnabled && keepAliveProduct;
    const isFromPLPPage = isClient() && window?.location?.href?.includes(SLASH_C_ROUTE);
    const categoryIdToCheck = currentCategoryId || categoryIdL3 || categoryId;
    // NOTE: isClearance is a string 'Clearance', not a boolean we should clean this up globally in the abstractor
    const sqnNmb = sqnNmbr;
    const itemBrand = brand;

    const isProductBrandOfSameDomain = this.getIsProductBrandSameDomain();
    const googleAnalyticsData = this.updateGoogleAnalyticsData;
    const portalName = this.getRecIdentifier();
    let divProps;
    const isPdp = this.isPDP();
    const imageBorder = isNewPLPImageBorderEnabled ? 'image-tile-border' : '';
    if (widgetScrapeContentId) {
      divProps = {
        className: `item-container-inner ${isPdp}`,
        theme,
        id: widgetScrapeContentId,
        isHorizontalPLPCarouselEnabled,
      };
    } else {
      divProps = {
        className: `item-container-inner ${isPdp}`,
        theme,
        isHorizontalPLPCarouselEnabled,
      };
    }

    return (
      <div
        className={`${className} horizontal-carousel-plp-product`}
        key={generalProductId}
        onMouseEnter={this.handleOpenAltImages}
        onMouseOut={this.handleCloseAltImages}
        onBlur={this.handleCloseAltImages}
        ref={forwardedRef}
      >
        <StyledItemContainerInner {...divProps}>
          <StyledProductImageContainer
            theme={theme}
            isSuggestedItem={isSuggestedItem}
            pdpUrl={pdpUrl}
            loadedProductCount={loadedProductCount}
            viaModule={viaModule}
            dataSource={dataSource}
            imageUrls={imageUrlsToShow}
            fromFavorites={fromFavorites}
            imageURL={imageURL}
            productName={prodNameAltImages}
            onImageChange={this.handleImageChange}
            colorsMap={colorsMap}
            analyticsData={{
              pId: generalProductId,
              prank: sqnNmb,
              requestId: unbxdId,
            }}
            keepAlive={keepAlive}
            isSoldOut={itemNotAvailable}
            soldOutLabel={outOfStockCapsLabels}
            itemBrand={itemBrand}
            isSearchPage={isSearchPage}
            page={page}
            isProductBrandOfSameDomain={isProductBrandOfSameDomain}
            isBundleProduct={bundleProduct}
            cookieToken={cookieToken}
            googleAnalyticsData={googleAnalyticsData}
            lowQualityEnabled={lowQualityEnabled}
            portalValue={portalValue && portalValue.length ? portalValue : portalName}
            dynamicBadgeOverlayQty={getDynamicBadgeQty(
              tcpStyleType,
              tcpStyleQty,
              isDynamicBadgeEnabled
            )}
            tcpStyleType={tcpStyleType}
            lazyLoad
            recToken={recToken}
            isSecureImageFlagEnabled={isSecureImageFlagEnabled}
            primaryBrand={primaryBrand || alternateBrand}
            alternateBrand={alternateBrand}
            isBopisFilterOn={isBopisFilterOn}
            categoryIdToCheck={categoryIdToCheck}
            setCurrentCategoryIdActn={setCurrentCategoryIdActn}
            isFromPLPPage={isFromPLPPage}
            isHorizontalPLPCarouselEnabled={isHorizontalPLPCarouselEnabled}
            className={`${isHorizontalPLPCarouselEnabled ? 'hr-plp' : ''} ${imageBorder}`}
            isPLPImageHeightBasedOnAspectRatio={isPLPImageHeightBasedOnAspectRatio}
            inGridPlpRecommendation={inGridPlpRecommendation}
            renderTopRatedBadgeItem={this.renderTopRatedBadgeItem}
            renderBestSellerBadgeItem={this.renderBestSellerBadgeItem}
            preloadImage={preloadImage}
            trackClick={() => {}}
          />
          <div>
            {this.renderCardTile()}
            {this.renderSubmitButton(
              keepAlive,
              itemNotAvailable,
              isHidePLPAddToBag,
              trackAnalyticsClick
            )}
            {this.errorMsgDisplay()}
            {this.renderSuggestedError(
              hasSuggestedProduct,
              suggestedProductErrors,
              generalProductId,
              item,
              isFavoriteView
            )}
          </div>
        </StyledItemContainerInner>
      </div>
    );
  }
}

const ProductsGridItemWithRef = forwardRef((props, ref) => {
  return <ProductsGridItem forwardedRef={ref} {...props} />;
});

export { ProductsGridItem as ProductsGridItemVanilla };

const ProductsGridItemStyled = withStyles(ProductsGridItemWithRef, styles);

// Display name is needed for hotfix mapping capability
ProductsGridItemStyled.displayName = 'ProductsGridItem';

export default ProductsGridItemStyled;
